import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, SignOut, LinkSimple, Copy, Trash, PencilSimple, Eye, EyeSlash,
  Plus, WarningCircle, Envelope, ChartLineUp, Link as LinkIcon, Prohibit, X, ChartBar, ShieldWarning, QrCode, ChartPieSlice, Stack,
} from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AnalyticsDialog from "@/pages/AnalyticsDialog";
import QRDialog from "@/pages/QRDialog";
import BulkDialog from "@/pages/BulkDialog";

const shortBase = window.location.origin;
function shortUrl(l) {
  try {
    if (l.short_url) {
      const h = new URL(l.short_url).hostname;
      if (h.includes(".") && !h.includes("localhost")) return l.short_url;
    }
  } catch { /* fall through */ }
  return `${shortBase}/r/${l.code}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dest, setDest] = useState("");
  const [alias, setAlias] = useState("");
  const [expires, setExpires] = useState("");
  const [domain, setDomain] = useState("");
  const [campaign, setCampaign] = useState("");
  const [domains, setDomains] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [qr, setQr] = useState(null);
  const [bulk, setBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, d] = await Promise.all([api.get("/links"), api.get("/domains")]);
      setLinks(l.data);
      setDomains(d.data);
    } catch { toast.error("Failed to load links"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { destination_url: dest };
      if (alias.trim()) payload.custom_alias = alias.trim();
      if (expires) payload.expires_at = new Date(expires).toISOString();
      if (domain) payload.domain = domain;
      if (campaign.trim()) payload.campaign = campaign.trim();
      const { data } = await api.post("/links", payload);
      setLinks([data, ...links]);
      setDest(""); setAlias(""); setExpires(""); setCampaign("");
      try { await navigator.clipboard.writeText(shortUrl(data)); } catch { /* clipboard may be blocked */ }
      toast.success("Short link created & copied to clipboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const copy = async (l) => {
    try { await navigator.clipboard.writeText(shortUrl(l)); toast.success("Short link copied"); }
    catch { toast.error("Copy blocked — select the link and copy manually"); }
  };

  const toggleDisabled = async (l) => {
    try {
      const { data } = await api.patch(`/links/${l.link_id}`, { disabled: !l.disabled });
      setLinks(links.map((x) => (x.link_id === l.link_id ? data : x)));
      toast.success(data.disabled ? "Link disabled" : "Link enabled");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete /r/${l.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/links/${l.link_id}`);
      setLinks(links.filter((x) => x.link_id !== l.link_id));
      toast.success("Link deleted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification link generated (check server logs in dev)."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const activeCount = links.filter((l) => l.status === "active").length;
  const campaignList = [...new Set(links.map((l) => l.campaign).filter(Boolean))].sort();
  const visibleLinks = campaignFilter ? links.filter((l) => l.campaign === campaignFilter) : links;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* header */}
      <header className="sticky top-0 z-40 bg-[#050505] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-white/30 flex items-center justify-center">
              <ShieldCheck size={18} weight="bold" />
            </div>
            <span className="font-head font-extrabold tracking-tighter text-lg">SAFESHORT</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block mono text-xs text-zinc-500">{user?.email}</span>
            <Link to="/campaigns" data-testid="campaigns-nav-link"
              className="flex items-center gap-2 mono text-xs uppercase tracking-wider border border-white/15 px-3 h-9 hover:border-white transition-colors">
              <ChartPieSlice size={15} /> Campaigns
            </Link>
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="admin-nav-link"
                className="flex items-center gap-2 mono text-xs uppercase tracking-wider border border-yellow-500/40 text-yellow-400 px-3 h-9 hover:bg-yellow-500/10 transition-colors">
                <ShieldWarning size={15} /> Moderation
              </Link>
            )}
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center gap-2 mono text-xs uppercase tracking-wider border border-white/15 px-3 h-9 hover:border-white transition-colors">
              <SignOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* verify banner */}
        {user && !user.email_verified && (
          <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3 justify-between fade-up" data-testid="verify-banner">
            <div className="flex items-center gap-3">
              <Envelope size={20} className="text-yellow-500 shrink-0" />
              <p className="text-sm text-yellow-200/90">
                Your email is unverified. Links from unverified accounts always show an interstitial warning.
              </p>
            </div>
            <button onClick={resend} data-testid="resend-verify-btn"
              className="mono text-xs uppercase tracking-wider border border-yellow-500/40 text-yellow-400 px-4 h-9 hover:bg-yellow-500/10 transition-colors shrink-0">
              Resend verification
            </button>
          </div>
        )}

        {/* title + stats */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <p className="label-xs mb-2">// dashboard</p>
            <h1 className="font-head text-3xl sm:text-4xl font-black tracking-tighter uppercase">Your links</h1>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            <Stat icon={<LinkIcon size={16} />} label="Links" value={links.length} testid="stat-links" />
            <Stat icon={<ChartLineUp size={16} />} label="Clicks" value={totalClicks} testid="stat-clicks" />
            <Stat icon={<Eye size={16} />} label="Active" value={activeCount} testid="stat-active" />
          </div>
        </div>

        {/* create form */}
        <form onSubmit={create} className="border border-white/10 bg-[#0a0a0a] p-5 mb-10" data-testid="create-form">
          <div className="flex items-center justify-between mb-4">
            <p className="label-xs">// new short link</p>
            <button type="button" onClick={() => setBulk(true)} data-testid="open-bulk-btn"
              className="flex items-center gap-2 mono text-xs uppercase tracking-wider border border-white/15 px-3 h-8 hover:border-white transition-colors">
              <Stack size={14} /> Bulk import
            </button>
          </div>
          <div className="grid lg:grid-cols-[1fr_auto] gap-3 mb-3">
            <div>
              <span className="label-xs block mb-2">Destination URL</span>
              <input value={dest} onChange={(e) => setDest(e.target.value)} required placeholder="https://example.com/very/long/path"
                data-testid="destination-input"
                className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={creating} data-testid="create-link-btn"
                className="w-full lg:w-auto h-11 px-6 bg-white text-black font-head font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-zinc-300 transition-colors disabled:opacity-50">
                <Plus size={16} weight="bold" /> {creating ? "..." : "Shorten"}
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <span className="label-xs block mb-2">Domain</span>
              <select value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="domain-select"
                className="w-full h-11 bg-[#0a0a0a] border border-white/10 px-3 text-sm mono text-zinc-300 focus:outline-none focus:border-white transition-colors">
                <option value="">Default {domains.find((d) => d.is_default) ? `(${domains.find((d) => d.is_default).domain})` : ""}</option>
                {domains.map((d) => (
                  <option key={d.domain} value={d.domain}>{d.domain}{d.health === "down" ? " (down)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="label-xs block mb-2">Campaign <span className="text-zinc-700">(opt)</span></span>
              <input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="launch-q3"
                data-testid="campaign-input"
                className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
            </div>
            <div>
              <span className="label-xs block mb-2">Custom path <span className="text-zinc-700">(opt)</span></span>
              <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="spring/sale-50" maxLength={60}
                data-testid="alias-input"
                className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
            </div>
            <div>
              <span className="label-xs block mb-2">Expires <span className="text-zinc-700">(opt)</span></span>
              <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)}
                data-testid="expires-input"
                className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono text-zinc-300 focus:outline-none focus:border-white transition-colors [color-scheme:dark]" />
            </div>
          </div>
        </form>

        {/* campaign filter */}
        {campaignList.length > 0 && (
          <div className="flex items-center gap-3 mb-4" data-testid="campaign-filter-bar">
            <span className="label-xs">// filter campaign</span>
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="campaign-filter"
              className="h-9 bg-[#0a0a0a] border border-white/10 px-3 text-xs mono text-zinc-300 focus:outline-none focus:border-white transition-colors">
              <option value="">All campaigns</option>
              {campaignList.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {/* table */}
        <div className="border border-white/10 overflow-x-auto" data-testid="links-table">
          <table className="w-full text-sm border-collapse min-w-[980px]">
            <thead>
              <tr className="label-xs text-left">
                <Th>Short link</Th><Th>Destination</Th><Th>Campaign</Th><Th className="text-right">Clicks</Th>
                <Th>Created</Th><Th>Status</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-8 text-center mono text-zinc-500">loading<span className="cursor-blink">_</span></td></tr>}
              {!loading && visibleLinks.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center">
                  <LinkSimple size={28} className="text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">No links yet. Create your first short link above.</p>
                </td></tr>
              )}
              {visibleLinks.map((l) => (
                <tr key={l.link_id} className="border-t border-white/10 hover:bg-white/[0.02] transition-colors" data-testid={`link-row-${l.code}`}>
                  <td className="p-3 border-r border-white/10">
                    <div className="flex items-center gap-2">
                      <a href={shortUrl(l)} target="_blank" rel="noreferrer" className="mono text-white hover:underline">/r/{l.code}</a>
                      <button onClick={() => copy(l)} data-testid={`copy-btn-${l.code}`} className="text-zinc-500 hover:text-white transition-colors"><Copy size={15} /></button>
                    </div>
                    {l.short_domain && <div className="mono text-[10px] text-zinc-600 mt-1 truncate">{l.short_domain}</div>}
                  </td>
                  <td className="p-3 border-r border-white/10 max-w-[260px]">
                    <div className="mono text-xs text-zinc-300 truncate">{l.destination_domain}</div>
                    <div className="text-xs text-zinc-600 truncate">{l.destination_url}</div>
                  </td>
                  <td className="p-3 border-r border-white/10">
                    {l.campaign
                      ? <span className="mono text-[10px] uppercase tracking-widest border border-white/20 text-zinc-300 px-2 py-0.5">{l.campaign}</span>
                      : <span className="text-zinc-700 text-xs">—</span>}
                  </td>
                  <td className="p-3 border-r border-white/10 text-right mono tabular-nums">{l.clicks}</td>
                  <td className="p-3 border-r border-white/10 mono text-xs text-zinc-400">{fmtDate(l.created_at)}</td>
                  <td className="p-3 border-r border-white/10"><StatusBadge status={l.status} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <IconBtn onClick={() => setQr(l)} testid={`qr-btn-${l.code}`} title="QR code"><QrCode size={15} /></IconBtn>
                      <IconBtn onClick={() => setAnalytics(l)} testid={`analytics-btn-${l.code}`} title="Analytics"><ChartBar size={15} /></IconBtn>
                      <IconBtn onClick={() => setEditing(l)} testid={`edit-btn-${l.code}`} title="Edit"><PencilSimple size={15} /></IconBtn>
                      <IconBtn onClick={() => toggleDisabled(l)} testid={`toggle-btn-${l.code}`} title={l.disabled ? "Enable" : "Disable"}>
                        {l.disabled ? <Eye size={15} /> : <EyeSlash size={15} />}
                      </IconBtn>
                      <IconBtn onClick={() => remove(l)} testid={`delete-btn-${l.code}`} title="Delete" danger><Trash size={15} /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {editing && <EditDialog link={editing} onClose={() => setEditing(null)} onSaved={(d) => { setLinks(links.map((x) => x.link_id === d.link_id ? d : x)); setEditing(null); }} />}
      {analytics && <AnalyticsDialog link={analytics} onClose={() => setAnalytics(null)} />}
      {qr && <QRDialog link={qr} url={shortUrl(qr)} onClose={() => setQr(null)} />}
      {bulk && <BulkDialog domains={domains} onClose={() => setBulk(false)} onCreated={(created) => setLinks((prev) => [...created, ...prev])} />}
    </div>
  );
}

function EditDialog({ link, onClose, onSaved }) {
  const [dest, setDest] = useState(link.destination_url);
  const [expires, setExpires] = useState(link.expires_at ? toLocalInput(link.expires_at) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { destination_url: dest, expires_at: expires ? new Date(expires).toISOString() : "" };
      const { data } = await api.patch(`/links/${link.link_id}`, payload);
      toast.success("Link updated");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="w-full max-w-lg border border-white/15 bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-head text-lg font-bold">Edit /r/{link.code}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <span className="label-xs block mb-2">Destination URL</span>
            <input value={dest} onChange={(e) => setDest(e.target.value)} data-testid="edit-destination-input"
              className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono focus:outline-none focus:border-white transition-colors" />
          </div>
          <div>
            <span className="label-xs block mb-2">Expiration (clear to remove)</span>
            <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="edit-expires-input"
              className="w-full h-11 bg-transparent border border-white/10 px-3 text-sm mono text-zinc-300 focus:outline-none focus:border-white transition-colors [color-scheme:dark]" />
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onClose} className="px-4 h-10 border border-white/20 mono text-xs uppercase tracking-wider hover:border-white transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-edit-btn" className="px-4 h-10 bg-white text-black mono text-xs uppercase tracking-wider hover:bg-zinc-300 transition-colors disabled:opacity-50">{saving ? "..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`p-3 border-r border-white/10 font-normal ${className}`}>{children}</th>;

function Stat({ icon, label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] px-5 py-3 min-w-[90px]" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 mb-1">{icon}<span className="label-xs">{label}</span></div>
      <div className="font-head text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, testid, title, danger }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
      className={`w-8 h-8 flex items-center justify-center border border-white/10 transition-colors ${danger ? "hover:border-red-500 hover:text-red-500" : "hover:border-white"}`}>
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "text-green-400 border-green-500/40",
    expired: "text-yellow-400 border-yellow-500/40",
    disabled: "text-zinc-400 border-white/20",
    blocked: "text-red-400 border-red-500/40",
  };
  return <span className={`mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${map[status] || map.disabled}`}>{status}</span>;
}

function SafeBadge({ s, reports }) {
  if (reports > 0) return <span className="mono text-[10px] uppercase tracking-widest border px-2 py-0.5 text-red-400 border-red-500/40 flex items-center gap-1 w-fit"><WarningCircle size={11} />{reports} report{reports > 1 ? "s" : ""}</span>;
  if (s === "malicious") return <span className="mono text-[10px] uppercase tracking-widest border px-2 py-0.5 text-red-400 border-red-500/40 flex items-center gap-1 w-fit"><Prohibit size={11} />blocked</span>;
  return <span className="mono text-[10px] uppercase tracking-widest border px-2 py-0.5 text-green-400 border-green-500/40 flex items-center gap-1 w-fit"><ShieldCheck size={11} />safe</span>;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "2-digit" }); }
  catch { return "—"; }
}
function toLocalInput(iso) {
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
